import { useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box, Typography, Select, MenuItem, IconButton } from '@mui/material';
import dayjs from 'dayjs';

const MINUTE_STEPS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const roundToStep = (minute) =>
  MINUTE_STEPS.reduce((prev, curr) =>
    Math.abs(curr - minute) < Math.abs(prev - minute) ? curr : prev
  );

const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const timeSx = {
  ...inputSx,
  bgcolor: 'white',
  fontSize: '0.82rem',
  '& .MuiSelect-select': { py: '6px', textAlign: 'center' },
};

const TimeSelect = ({ label, value, onChange, options }) => (
  <Box sx={{ width: 62, flexShrink: 0 }}>
    <Typography sx={{ fontSize: '0.68rem', color: '#6b7280', fontWeight: 500, mb: 0.3, textAlign: 'center' }}>
      {label}
    </Typography>
    <Select size="small" fullWidth value={value} onChange={(e) => onChange(e.target.value)} sx={timeSx}>
      {options.map((opt) => (
        <MenuItem key={opt} value={opt} sx={{ fontSize: '0.82rem', minHeight: 'unset', py: '3px', justifyContent: 'center' }}>
          {String(opt).padStart(2, '0')}
        </MenuItem>
      ))}
    </Select>
  </Box>
);

const ExpiryDateTimePicker = ({ label, value, onChange, slotPropsTextField = {} }) => {
  const [openDate, setOpenDate] = useState(false);
  const dayjsVal = value ? dayjs(value) : null;

  const handleDateChange = (newDate) => {
    if (!newDate || !newDate.isValid()) { onChange(''); return; }
    const h = dayjsVal?.hour() ?? 20;
    const m = dayjsVal ? roundToStep(dayjsVal.minute()) : 0;
    onChange(newDate.hour(h).minute(m).second(0).toISOString());
  };

  return (
    <Box sx={slotPropsTextField?.sx} dir="rtl">
      <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 600, mb: 0.75 }}>
        {label}
      </Typography>

      {/* שורה 1: DatePicker */}
      <DatePicker
        value={dayjsVal}
        onChange={handleDateChange}
        open={openDate}
        onOpen={() => setOpenDate(true)}
        onClose={() => setOpenDate(false)}
        slotProps={{
          field: { readOnly: true },
          textField: {
            size: 'small',
            fullWidth: true,
            InputLabelProps: { shrink: true },
            inputProps: { style: { cursor: 'pointer' } },
            onClick: () => setOpenDate(true),
            sx: { ...inputSx, bgcolor: 'white' },
          },
        }}
      />

      {/* שורה 2: שעה + דקות + ✕ — מיושרים לימין, מרווח קטן מלמעלה */}
      {dayjsVal && (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, mt: 0.75, justifyContent: 'flex-end' }} dir="ltr">
          <TimeSelect
            label="שעה"
            value={dayjsVal.hour()}
            onChange={(h) => onChange(dayjsVal.hour(Number(h)).second(0).toISOString())}
            options={Array.from({ length: 24 }, (_, i) => i)}
          />
          <TimeSelect
            label="דקות"
            value={roundToStep(dayjsVal.minute())}
            onChange={(m) => onChange(dayjsVal.minute(Number(m)).second(0).toISOString())}
            options={MINUTE_STEPS}
          />
          <IconButton
            size="small"
            onClick={() => onChange('')}
            title="הסר תפוגה"
            sx={{ color: '#9ca3af', mb: '1px', flexShrink: 0, '&:hover': { color: '#ef4444' } }}
          >
            ✕
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default ExpiryDateTimePicker;
